import { nanoid } from 'nanoid';

import {
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  QUESTION_TYPES,
} from '@api/instant_survey';

import { toast } from '@utilities';

import { DEFAULT_QUESTION_GROUP_NAME } from '../../../utilities/constants';
import { isQuestionGroupNameTaken } from '../../../utilities/helpers';

/**
 * Cleans up option logic references to a specific question/answer combination
 * @param {Object} modifiedQuestionLogic - The question logic object to modify
 * @param {string|number} targetQuestionId - The question ID to search for
 * @param {Set} deletedAnswerIds - Set of answer IDs that were deleted (optional)
 * @returns {boolean} Whether any changes were made
 */
const cleanupOptionLogicReferences = (
  modifiedQuestionLogic,
  targetQuestionId,
  deletedAnswerIds = null
) => {
  let hasChanges = false;

  Object.keys(modifiedQuestionLogic).forEach((questionKey) => {
    if (!modifiedQuestionLogic[questionKey].option) return;

    Object.keys(modifiedQuestionLogic[questionKey].option).forEach((answerKey) => {
      const optionLogicForAnswer = modifiedQuestionLogic[questionKey].option[answerKey];

      if (!optionLogicForAnswer.logic) return;

      const originalLength = optionLogicForAnswer.logic.length;

      // Filter logic rows based on whether we're removing entire question or specific answers
      optionLogicForAnswer.logic = optionLogicForAnswer.logic.filter((logicRow) => {
        // Keep logic rows that reference a different question
        if (String(logicRow.question?.id) !== String(targetQuestionId)) return true;

        // If we have deleted answer IDs, filter them out from the answers array.
        // Numeric/quota-group rows have no `answers` array — they aren't
        // answer-based, so deleting specific answers must not touch them.
        if (deletedAnswerIds) {
          if (!Array.isArray(logicRow.answers)) return true;

          logicRow.answers = logicRow.answers.filter((ans) => !deletedAnswerIds.has(ans.id));
          return logicRow.answers.length > 0;
        }
        return false;
      });

      if (optionLogicForAnswer.logic.length !== originalLength) {
        hasChanges = true;
      }

      if (optionLogicForAnswer.logic.length === 0) {
        delete modifiedQuestionLogic[questionKey].option[answerKey];
      }
    });

    if (Object.keys(modifiedQuestionLogic[questionKey].option).length === 0) {
      delete modifiedQuestionLogic[questionKey].option;
    }
  });

  return hasChanges;
};

/**
 * Creates an independent local copy of a sequential monadic sub-question.
 * @param {Object} subQuestion
 * @returns {Object}
 */
export const duplicateSubQuestion = ({
  _delete,
  answers = [],
  id,
  noneAnswer,
  subQuestions = [],
  translations,
  ...subQuestion
}) => ({
  ...subQuestion,
  answers: answers
    .filter(({ _delete: isDeleted }) => !isDeleted)
    .map(({ _delete, id, translations, ...answer }) => ({
      ...answer,
      id: nanoid(),
      translations: translations
        ?.filter(({ _delete: isDeleted }) => !isDeleted)
        .map(({ _delete, id, ...translation }) => ({
          ...translation,
          id: nanoid(),
        })),
    })),
  id: nanoid(),
  noneAnswer: noneAnswer
    ? {
        ...noneAnswer,
        id: nanoid(),
        translations: noneAnswer.translations?.map(({ id, ...translation }) => ({
          ...translation,
          id: nanoid(),
        })),
      }
    : noneAnswer,
  subQuestions: subQuestions
    .filter(({ _delete: isDeleted }) => !isDeleted)
    .map(({ _delete, id, translations, ...subQuestion }) => ({
      ...subQuestion,
      id: nanoid(),
      translations: translations
        ?.filter(({ _delete: isDeleted }) => !isDeleted)
        .map(({ _delete, id, ...translation }) => ({
          ...translation,
          id: nanoid(),
        })),
    })),
  translations: translations?.map(({ id, ...translation }) => ({
    ...translation,
    id: nanoid(),
  })),
});

/**
 * Checks to see if any question logic needs to be changed
 * @param {Object} options.originalQuestion
 * @param {Object} options.question
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 */
export const cleanupLogicAfterEdit = ({ originalQuestion, question, questionLogic, questions }) => {
  let hasLogicChanges = false;
  const modifiedQuestionLogic = { ...questionLogic };

  // recommended question became a custom question, treat it as a removal
  if (originalQuestion.id !== question.id)
    return cleanupLogicAfterRemoval({
      question: originalQuestion,
      questionId: originalQuestion.id,
      questionLogic,
      questions,
    });

  const disqualifyLogic = modifiedQuestionLogic[question.id]?.disqualify;

  if (disqualifyLogic) {
    if (
      [QUESTION_TYPES.ESSAY.value, QUESTION_TYPES['RANK-DRAGDROP'].value].includes(question.type)
    ) {
      // cannot disqualify if the question type is essay or rank
      delete modifiedQuestionLogic[question.id].disqualify;
      hasLogicChanges = true;
    } else if (
      Array.isArray(disqualifyLogic.logic)
        ? disqualifyLogic.logic.some((row) => (row.answers || row.values || []).length > 0)
        : (disqualifyLogic.answers || []).length > 0
    ) {
      // clear answers/values to prevent prior selections from 'sticking'

      if (shouldClearQuestionLogic(question, originalQuestion)) {
        if (Array.isArray(disqualifyLogic.logic)) {
          modifiedQuestionLogic[question.id].disqualify.logic = modifiedQuestionLogic[
            question.id
          ].disqualify.logic.map((row) => ({
            ...row,
            answers: row.answers !== undefined ? [] : undefined,
            values: row.values !== undefined ? [] : undefined,
          }));
        } else {
          modifiedQuestionLogic[question.id].disqualify.answers = [];
        }
        hasLogicChanges = true;
      }
    }
  }

  Object.keys(modifiedQuestionLogic).forEach((id) => {
    const skipLogic = modifiedQuestionLogic[id].skip?.logic;
    if (skipLogic) {
      skipLogic.forEach((row) => {
        if (row.question?.id === question.id) {
          if (!shouldClearQuestionLogic(question, row.question)) {
            return;
          }

          row.answers = [];
          row.question = [
            QUESTION_TYPES.ESSAY.value,
            QUESTION_TYPES.NUMERIC.value,
            QUESTION_TYPES['RANK-DRAGDROP'].value,
          ].includes(question.type)
            ? undefined
            : question;
          hasLogicChanges = true;
        }
      });
    }
  });

  // Clean up option logic when answers are deleted
  const deletedAnswerIds = new Set(
    originalQuestion.answers
      .filter((origAnswer) => !question.answers.some((ans) => ans.id === origAnswer.id))
      .map((ans) => ans.id)
  );

  if (deletedAnswerIds.size > 0) {
    const optionLogicChanged = cleanupOptionLogicReferences(
      modifiedQuestionLogic,
      question.id,
      deletedAnswerIds
    );
    hasLogicChanges = hasLogicChanges || optionLogicChanged;
  }

  if (hasLogicChanges) displayQuestionLogicUpdatedToast();
  return modifiedQuestionLogic;
};

const shouldClearQuestionLogic = (question, originalQuestion) => {
  if (question.type !== originalQuestion.type) {
    return true;
  }

  // Check if any selectable options were removed/edited
  return originalQuestion.answers.some(({ id: originalId, value: originalValue }) => {
    const matchingNewOption = question.answers.find(
      ({ id, value }) => originalId === id && originalValue === value
    );
    return !matchingNewOption;
  });
};

/**
 * Checks to see if any question logic needs to be changed
 * @param {Object} options.question
 * @param {number} options.questionId
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 * @param {boolean} [options.shouldDisplayToast]
 */
export const cleanupLogicAfterRemoval = ({
  question,
  questionId,
  questionLogic,
  questions,
  shouldDisplayToast = true,
}) => {
  let hasLogicChanges = false;
  const modifiedQuestionLogic = { ...questionLogic };
  const questionIdsToRemove = new Set([
    String(questionId),
    ...(question?.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value
      ? (question.subQuestions || []).map(({ id }) => String(id))
      : []),
  ]);

  // remove logic for question that was removed
  questionIdsToRemove.forEach((idToRemove) => {
    if (modifiedQuestionLogic[idToRemove]?.disqualify || modifiedQuestionLogic[idToRemove]?.skip) {
      delete modifiedQuestionLogic[idToRemove];
      hasLogicChanges = true;
    }
    // remove option logic for question that was removed
    if (modifiedQuestionLogic[idToRemove]?.option) {
      delete modifiedQuestionLogic[idToRemove].option;
      hasLogicChanges = true;
    }
  });

  Object.keys(modifiedQuestionLogic).forEach((id) => {
    const skipLogic = modifiedQuestionLogic[id].skip?.logic;
    if (skipLogic) {
      // if the removal of a question moved this question to the first postion, remove skip logic
      if (questions.findIndex((question) => String(id) === String(question.id)) === 0) {
        delete modifiedQuestionLogic[id]?.skip;
        hasLogicChanges = true;
        return;
      }

      // remove any skip logic that references the removed question
      // Check if the question has an id, as there are some cases the logic is for quota groups not questions
      const updatedSkipLogic = skipLogic.filter(
        (row) => !questionIdsToRemove.has(String(row.question?.id))
      );
      if (updatedSkipLogic.length !== skipLogic.length) {
        if (updatedSkipLogic.length === 0) {
          delete modifiedQuestionLogic[id].skip;
        } else {
          modifiedQuestionLogic[id].skip.logic = updatedSkipLogic;
        }
        hasLogicChanges = true;
      }
    }
  });

  // Clean up option logic that references the removed question(s)
  questionIdsToRemove.forEach((idToRemove) => {
    const optionLogicChanged = cleanupOptionLogicReferences(modifiedQuestionLogic, idToRemove);
    hasLogicChanges = hasLogicChanges || optionLogicChanged;
  });

  const questionGroupLogic = modifiedQuestionLogic[QUESTION_GROUP_LOGIC];
  if (questionGroupLogic) {
    const updatedQuestionGroupLogic = { ...questionGroupLogic };

    Object.keys(updatedQuestionGroupLogic).forEach((groupId) => {
      let updatedGroup = updatedQuestionGroupLogic[groupId];
      // pruneInvalidLogic preserves a falsy group entry as-is, so it can reach here.
      if (!updatedGroup) return;

      QUESTION_GROUP_LOGIC_BRANCHES.forEach((branch) => {
        const branchLogic = updatedGroup[branch]?.logic;
        if (!branchLogic) return;

        const updatedBranchLogic = branchLogic.filter(
          (row) => !questionIdsToRemove.has(String(row.question?.id))
        );
        if (updatedBranchLogic.length === branchLogic.length) return;

        hasLogicChanges = true;
        updatedGroup = { ...updatedGroup };
        if (updatedBranchLogic.length === 0) {
          delete updatedGroup[branch];
        } else {
          updatedGroup[branch] = { ...updatedGroup[branch], logic: updatedBranchLogic };
        }
      });

      // A group whose every branch lost all its rows loses its entry entirely.
      if (Object.keys(updatedGroup).length === 0) {
        delete updatedQuestionGroupLogic[groupId];
      } else {
        updatedQuestionGroupLogic[groupId] = updatedGroup;
      }
    });

    if (Object.keys(updatedQuestionGroupLogic).length === 0) {
      delete modifiedQuestionLogic[QUESTION_GROUP_LOGIC];
    } else {
      modifiedQuestionLogic[QUESTION_GROUP_LOGIC] = updatedQuestionGroupLogic;
    }
  }

  if (hasLogicChanges && shouldDisplayToast) displayQuestionLogicUpdatedToast();
  return modifiedQuestionLogic;
};

/**
 * Shows a toast letting users know question logic was changed after a questionnaire edit.
 * @returns {void}
 */
export const displayQuestionLogicUpdatedToast = () => {
  toast(
    <p>
      Some logic in this questionnaire has been updated or removed due to a survey question change.
    </p>
  );
};

/**
 * Remaps question logic for a set of copied questions onto their newly-created counterparts.
 * Mirrors the backend copy_question_logic: entries are re-keyed to the new question id and every
 * answer/question reference to a copied entity is swapped for its copy. References to entities that
 * were not copied are left pointing at the original.
 * @param {Object} options
 * @param {Map<number, Object>} options.answerMap old answer id -> new answer object
 * @param {Object} options.questionLogic in-memory question logic (post-transformQuestionLogic)
 * @param {Map<number, Object>} options.questionMap old question id -> new question object
 * @returns {Object} new logic entries keyed by the new question id
 */
export const remapCopiedQuestionLogic = ({ answerMap, questionLogic, questionMap }) => {
  const result = {};

  const remapAnswers = (answers = []) =>
    answers.map((answer) => answerMap.get(answer.id)).filter(Boolean);

  // Disqualify rows target the question's own answers (always copied); url_variable rows carry none.
  const remapDisqualifyRow = (row) =>
    row.url_variable ? row : { ...row, answers: remapAnswers(row.answers) };

  // Skip / option rows reference another question. Only remap when that question was also copied;
  // otherwise leave the row pointing at the original question and its answers.
  const remapReferenceRow = (row) => {
    if (row.url_variable) return row;
    if (!row.question || !questionMap.has(row.question.id)) return row;

    const newQuestion = questionMap.get(row.question.id);
    return Object.hasOwn(row, 'comparison_value')
      ? { ...row, question: newQuestion }
      : { ...row, answers: remapAnswers(row.answers), question: newQuestion };
  };

  for (const [oldQuestionId, newQuestion] of questionMap) {
    const logic = questionLogic?.[oldQuestionId];
    if (!logic) continue;

    const newEntry = {};

    if (logic.disqualify) {
      const { disqualify } = logic;
      newEntry.disqualify = Array.isArray(disqualify.logic)
        ? { ...disqualify, logic: disqualify.logic.map(remapDisqualifyRow) }
        : { ...disqualify, answers: remapAnswers(disqualify.answers) };
    }

    if (logic.skip) {
      newEntry.skip = { ...logic.skip, logic: logic.skip.logic.map(remapReferenceRow) };
    }

    if (logic.option) {
      const newOption = {};
      for (const [answerId, optionLogic] of Object.entries(logic.option)) {
        const newAnswer = answerMap.get(Number(answerId));
        if (!newAnswer) continue;
        newOption[newAnswer.id] = {
          ...optionLogic,
          logic: optionLogic.logic.map(remapReferenceRow),
        };
      }
      if (Object.keys(newOption).length > 0) newEntry.option = newOption;
    }

    result[newQuestion.id] = newEntry;
  }

  return result;
};

/**
 * Remaps carry-forward entries owned by copied questions. Sources copied with the group point to
 * their copies; sources outside the group continue pointing to the original question.
 * @param {Object} options
 * @param {Object} [options.carryForwardLogic]
 * @param {Map<number, Object>} options.questionMap old question id -> new question object
 * @returns {Object} new carry-forward entries keyed by the copied target question id
 */
export const remapCopiedCarryForwardLogic = ({ carryForwardLogic = {}, questionMap }) => {
  const result = {};

  for (const [oldQuestionId, newQuestion] of questionMap) {
    const logic = carryForwardLogic?.[oldQuestionId];
    if (!logic) continue;

    result[newQuestion.id] = {
      ...logic,
      sourceSurveyQuestionId:
        questionMap.get(logic.sourceSurveyQuestionId)?.id ?? logic.sourceSurveyQuestionId,
    };
  }

  return result;
};

/**
 * Lists a group and its descendant groups, parents always ahead of their children so a copy pass
 * can create a parent before the children that point at it. The builder currently caps nesting at
 * two levels, but the walk is depth-agnostic to match the backend copy service.
 * @param {Object} options
 * @param {Object} options.group the source QuestionGroup
 * @param {Object} [options.questionGroups] all question groups keyed by id
 * @returns {Object[]} the source group followed by its descendants
 */
export const collectGroupSubtree = ({ group, questionGroups = {} }) => {
  const subtree = [group];
  const seenGroupIds = new Set([group.id]);

  for (let index = 0; index < subtree.length; index += 1) {
    const { id: parentGroupId } = subtree[index];
    Object.values(questionGroups).forEach((candidate) => {
      if (candidate.parentGroup !== parentGroupId || seenGroupIds.has(candidate.id)) return;
      seenGroupIds.add(candidate.id);
      subtree.push(candidate);
    });
  }

  return subtree;
};

/**
 * firstGroupSubtreeQuestionIndex
 * Index of the first question belonging to `groupId` or any group nested beneath it — where the
 * group starts being asked. A parent group can hold nothing but child groups, so its own direct
 * members are not enough to locate it.
 * @param {Object} options
 * @param {Number|String} options.groupId
 * @param {Object} options.questionGroups all question groups, keyed by id (an array also works)
 * @param {Object[]} options.questions flat question list to search
 * @returns {Number} index into `questions`, or -1 when the subtree has no questions
 */
export const firstGroupSubtreeQuestionIndex = ({
  groupId,
  questionGroups = {},
  questions = [],
}) => {
  const group = Object.values(questionGroups).find(({ id }) => String(id) === String(groupId));
  if (!group) return -1;

  const subtreeGroupIds = new Set(
    collectGroupSubtree({ group, questionGroups }).map(({ id }) => String(id))
  );
  return questions.findIndex(({ questionGroup }) => subtreeGroupIds.has(String(questionGroup)));
};

/**
 * Duplicates a question group and everything nested inside it: creates a backend group per group in
 * the subtree (children re-parented onto their copied parent), creates a fresh copy of every member
 * question (real ids), and remaps the subtree's question logic onto those copies.
 * @param {Object} options
 * @param {Object} [options.carryForwardLogic]
 * @param {Function} options.createQuestion async ({ question }) => createdQuestion
 * @param {Function} options.createQuestionGroup async ({ name, parentGroup, randomizeOrder }) => group
 * @param {Object} options.group the source QuestionGroup
 * @param {Object} options.questionGroups all question groups (for subtree walk and name dedup)
 * @param {Object} options.questionLogic current in-memory question logic
 * @param {Object[]} options.questions all survey questions
 * @returns {Promise<{
 *   createdQuestions: Object[],
 *   newGroup: Object,
 *   newGroups: Object[],
 *   remappedCarryForwardLogic: Object,
 *   remappedLogic: Object,
 * }>}
 */
export const buildCopiedGroup = async ({
  carryForwardLogic,
  createQuestion,
  createQuestionGroup,
  group,
  questionGroups,
  questionLogic,
  questions,
}) => {
  const sourceGroups = collectGroupSubtree({ group, questionGroups });
  const sourceGroupIds = new Set(sourceGroups.map(({ id }) => id));
  const members = questions.filter(
    (question) => sourceGroupIds.has(question.questionGroup) && !question._delete
  );

  if (members.length === 0) {
    return {
      createdQuestions: [],
      newGroup: null,
      newGroups: [],
      remappedCarryForwardLogic: {},
      remappedLogic: {},
    };
  }

  // Names minted during this pass aren't in questionGroups yet, so dedup against both or sibling
  // groups sharing a base name would all land on "(copy)".
  const nameDedupGroups = { ...questionGroups };
  const nextCopiedGroupName = (sourceGroup) => {
    const trimmedGroupName = (sourceGroup.name || DEFAULT_QUESTION_GROUP_NAME).trim();
    let copiedGroupName = `${trimmedGroupName} (copy)`;
    let copiedGroupNameSuffix = 2;
    while (isQuestionGroupNameTaken({ name: copiedGroupName, questionGroups: nameDedupGroups })) {
      copiedGroupName = `${trimmedGroupName} (copy ${copiedGroupNameSuffix})`;
      copiedGroupNameSuffix += 1;
    }
    return copiedGroupName;
  };

  // Source group id -> copied group. Mirrors the backend's copied_question_group_map; because
  // collectGroupSubtree is parent-first, a child's copied parent is always already in the map.
  const copiedGroupMap = new Map();
  const newGroups = [];
  for (const sourceGroup of sourceGroups) {
    const copiedParentGroupId = copiedGroupMap.get(sourceGroup.parentGroup)?.id;
    // The subtree root keeps its original parent; nested groups follow their copied parent.
    const parentGroup = copiedParentGroupId ?? sourceGroup.parentGroup;
    const name = nextCopiedGroupName(sourceGroup);

    const createdGroup = await createQuestionGroup({
      name,
      ...(parentGroup != null ? { parentGroup } : {}),
      randomizeOrder: sourceGroup.randomizeOrder ?? false,
    });

    copiedGroupMap.set(sourceGroup.id, createdGroup);
    newGroups.push(createdGroup);
    nameDedupGroups[String(createdGroup.id)] = createdGroup;
  }

  const newGroup = copiedGroupMap.get(group.id);

  const createdQuestions = [];
  for (const member of members) {
    const { questionClientId: _clientId, questionGroup: _group, ...rest } = member;
    const created = await createQuestion({ question: stripQuestionIds(rest) });
    createdQuestions.push({
      ...created,
      questionGroup: copiedGroupMap.get(member.questionGroup).id,
    });
  }

  // Pair each source question and answer with its newly created counterpart so copied logic can
  // reference the new backend IDs. One map spans the whole subtree, so logic crossing the
  // parent/child boundary resolves onto the copies instead of the originals. Sequential-monadic
  // sub-questions are mapped recursively.
  const answerMap = new Map();
  const questionMap = new Map();
  const mapCopiedIds = (source, created) => {
    if (!source || !created) return;

    questionMap.set(source.id, created);
    (source.answers || []).forEach((answer, index) => {
      const createdAnswer = created.answers?.[index];
      if (createdAnswer) answerMap.set(answer.id, createdAnswer);
    });
    (source.subQuestions || []).forEach((subQuestion, index) =>
      mapCopiedIds(subQuestion, created.subQuestions?.[index])
    );
  };
  members.forEach((sourceQuestion, index) => mapCopiedIds(sourceQuestion, createdQuestions[index]));

  const remappedCarryForwardLogic = remapCopiedCarryForwardLogic({
    carryForwardLogic,
    questionMap,
  });
  const remappedLogic = remapCopiedQuestionLogic({ answerMap, questionLogic, questionMap });

  return { createdQuestions, newGroup, newGroups, remappedCarryForwardLogic, remappedLogic };
};

/**
 * Rebuilds a carry-forward destination question's answers so that its
 * carry-forward-generated options stay in sync with the (just edited) source
 * question's options.
 *
 *
 * @param {Object} options
 * @param {import('@api/instant_survey/types').Question} options.destinationQuestion - a question carrying forward from the source
 * @param {import('@api/instant_survey/types').Question} options.sourceQuestion - the just-edited source question
 * @returns {import('@api/instant_survey/types').Question} the destination question with synced answers
 */
export const syncCarryForwardAnswers = ({ destinationQuestion, sourceQuestion }) => {
  const eligibleSourceOptions = sourceQuestion.answers.filter(
    ({ _delete, isNoneOfTheAbove }) => !_delete && !isNoneOfTheAbove
  );
  const eligibleSourceIds = new Set(eligibleSourceOptions.map(({ id }) => id));

  const isGridDestination = destinationQuestion.type === QUESTION_TYPES['TABLE-RADIO'].value;
  if (isGridDestination) {
    const existingCarryForwardSubQuestions = destinationQuestion.subQuestions.filter(
      ({ isCarryForwardGenerated }) => isCarryForwardGenerated
    );
    const ownSubQuestions = destinationQuestion.subQuestions.filter(
      ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
    );
    const carryForwardSubQuestionBySourceId = new Map(
      existingCarryForwardSubQuestions.map((subQuestion) => [
        subQuestion.carriedFromOptionId,
        subQuestion,
      ])
    );

    // Refresh existing carry-forward rows and append new ones, following the
    // current source option order.
    const syncedCarryForwardSubQuestions = eligibleSourceOptions.map(
      ({ id: sourceOptionId, image, richText, text }) => {
        const existingSubQuestion = carryForwardSubQuestionBySourceId.get(sourceOptionId);
        return {
          carriedFromOptionId: sourceOptionId,
          excludeFromCarryForward: existingSubQuestion?.excludeFromCarryForward,
          id: existingSubQuestion?.id ?? nanoid(),
          image,
          isCarryForwardGenerated: true,
          richText,
          text,
          type: QUESTION_TYPES.RADIO.value,
        };
      }
    );

    // Soft-delete generated rows whose source option no longer exists so the
    // backend removes them (client-only rows can simply be dropped).
    const deletedCarryForwardSubQuestions = existingCarryForwardSubQuestions
      .filter(
        (subQuestion) =>
          !eligibleSourceIds.has(subQuestion.carriedFromOptionId) &&
          typeof subQuestion.id === 'number'
      )
      .map((subQuestion) => ({ ...subQuestion, _delete: true }));

    return {
      ...destinationQuestion,
      subQuestions: [
        ...syncedCarryForwardSubQuestions,
        ...ownSubQuestions,
        ...deletedCarryForwardSubQuestions,
      ],
    };
  }

  const existingCarryForwardAnswers = destinationQuestion.answers.filter(
    ({ isCarryForwardGenerated }) => isCarryForwardGenerated
  );
  const ownAnswers = destinationQuestion.answers.filter(
    ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
  );
  const carryForwardAnswerBySourceId = new Map(
    existingCarryForwardAnswers.map((answer) => [answer.carriedFromOptionId, answer])
  );

  // Refresh existing carry-forward answers and append new ones, following the
  // current source option order.
  const syncedCarryForwardAnswers = eligibleSourceOptions.map(
    ({ id: sourceOptionId, isAnchored, isExclusive, ...sourceOption }) => {
      const existingAnswer = carryForwardAnswerBySourceId.get(sourceOptionId);
      return {
        ...sourceOption,
        carriedFromOptionId: sourceOptionId,
        excludeFromCarryForward: existingAnswer?.excludeFromCarryForward,
        id: existingAnswer?.id ?? nanoid(),
        isCarryForwardGenerated: true,
      };
    }
  );

  // Soft-delete carry-forward answers whose source option no longer exists so
  // the backend removes them (client-only answers can simply be dropped).
  const deletedCarryForwardAnswers = existingCarryForwardAnswers
    .filter(
      (answer) =>
        !eligibleSourceIds.has(answer.carriedFromOptionId) && typeof answer.id === 'number'
    )
    .map((answer) => ({ ...answer, _delete: true }));

  return {
    ...destinationQuestion,
    answers: [...syncedCarryForwardAnswers, ...ownAnswers, ...deletedCarryForwardAnswers],
  };
};

export const stripQuestionIds = (question) => {
  const { answers = [], id: _, subQuestions = [], translations = [], ...rest } = question;

  return {
    ...rest,
    answers: answers.map(({ id: _, translations = [], ...answer }) => ({
      ...answer,
      translations: translations.map(({ id: _, ...translation }) => translation),
    })),
    subQuestions: subQuestions.map(stripQuestionIds),
    translations: translations.map(({ id: _, ...translation }) => translation),
  };
};
